import { useCallback, useEffect, useState } from 'react';
import { getAccessToken } from '../utils/auth';
import { getIncompleteAircraft, updateAircraft } from '../utils/httpApi';
import { isApiSuccess, getFirstApiError } from '../utils/api';
import './Admin.css';

const EMPTY_EDIT = {
  registration: '',
  aircraftType: '',
  manufacturer: '',
  model: '',
  operator: '',
  owner: '',
  notes: ''
};

function AdminAircraftGaps() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingIcao24, setEditingIcao24] = useState(null);
  const [edit, setEdit] = useState(EMPTY_EDIT);
  const [saveMsg, setSaveMsg] = useState(null);
  const [recentDays, setRecentDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getAccessToken();
      const resp = await getIncompleteAircraft({ recentDays, limit: 200 }, token);
      if (isApiSuccess(resp)) {
        setRows(resp.rows || []);
      } else {
        setError(getFirstApiError(resp) || 'Failed to load aircraft gaps');
      }
    } catch (e) {
      setError('Failed to load aircraft gaps');
    } finally {
      setLoading(false);
    }
  }, [recentDays]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (row) => {
    setEditingIcao24(row.icao24);
    setEdit({
      registration: row.registration || '',
      aircraftType: row.aircraftType || '',
      manufacturer: row.manufacturer || '',
      model: row.model || '',
      operator: row.operator || '',
      owner: row.owner || '',
      notes: ''
    });
    setSaveMsg(null);
  };

  const save = async () => {
    if (!editingIcao24) return;
    const payload = { icao24: editingIcao24 };
    Object.keys(edit).forEach(k => {
      if (edit[k] !== '') payload[k] = edit[k];
    });
    try {
      const token = getAccessToken();
      const resp = await updateAircraft(payload, token);
      if (isApiSuccess(resp) && resp.updated) {
        setSaveMsg({ type: 'success', text: `Updated ${editingIcao24}` });
        setEditingIcao24(null);
        load();
      } else {
        setSaveMsg({ type: 'error', text: getFirstApiError(resp) || 'Update failed' });
      }
    } catch (e) {
      setSaveMsg({ type: 'error', text: 'Update failed' });
    }
  };

  if (loading) return <div className="admin-loading">Loading aircraft gaps...</div>;
  if (error) {
    return (
      <div className="admin-error">
        <p>{error}</p>
        <button onClick={load}>Retry</button>
      </div>
    );
  }

  return (
    <div className="admin-table-container">
      <div className="admin-toolbar">
        <label>
          Last seen within:
          <select value={recentDays} onChange={e => setRecentDays(parseInt(e.target.value, 10))}>
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={365}>1 year</option>
            <option value={3650}>All</option>
          </select>
        </label>
        <button onClick={load}>Refresh</button>
        <span className="admin-toolbar-meta">{rows.length} aircraft with gaps</span>
      </div>

      {saveMsg && (
        <div className={`admin-action-message admin-action-${saveMsg.type}`}>
          {saveMsg.text}
        </div>
      )}

      <table className="admin-table">
        <thead>
          <tr>
            <th>ICAO24</th>
            <th>Registration</th>
            <th>Callsign</th>
            <th>Type</th>
            <th>Manufacturer</th>
            <th>Model</th>
            <th>Operator</th>
            <th>Tracks</th>
            <th>Last Seen</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const isEditing = editingIcao24 === row.icao24;
            return (
              <>
                <tr key={row.icao24} className={isEditing ? 'expanded' : ''}>
                  <td data-label="ICAO24"><code>{row.icao24}</code></td>
                  <td data-label="Registration">{row.registration || <span className="gap">—</span>}</td>
                  <td data-label="Callsign">{row.callsign || <span className="gap">—</span>}</td>
                  <td data-label="Type">{row.aircraftType || <span className="gap">—</span>}</td>
                  <td data-label="Manufacturer">{row.manufacturer || <span className="gap">—</span>}</td>
                  <td data-label="Model">{row.model || <span className="gap">—</span>}</td>
                  <td data-label="Operator">{row.operator || <span className="gap">—</span>}</td>
                  <td data-label="Tracks">{row.totalTracks || 0}</td>
                  <td data-label="Last Seen">{row.lastSeenAt ? new Date(row.lastSeenAt).toLocaleDateString() : '—'}</td>
                  <td>
                    <button onClick={() => isEditing ? setEditingIcao24(null) : startEdit(row)}>
                      {isEditing ? 'Cancel' : 'Edit'}
                    </button>
                  </td>
                </tr>
                {isEditing && (
                  <tr key={`${row.icao24}-edit`} className="receivers-row">
                    <td colSpan={10}>
                      <div className="admin-edit-form">
                        <label>Registration<input value={edit.registration} onChange={e => setEdit({ ...edit, registration: e.target.value })} /></label>
                        <label>Type<input value={edit.aircraftType} onChange={e => setEdit({ ...edit, aircraftType: e.target.value })} /></label>
                        <label>Manufacturer<input value={edit.manufacturer} onChange={e => setEdit({ ...edit, manufacturer: e.target.value })} /></label>
                        <label>Model<input value={edit.model} onChange={e => setEdit({ ...edit, model: e.target.value })} /></label>
                        <label>Operator<input value={edit.operator} onChange={e => setEdit({ ...edit, operator: e.target.value })} /></label>
                        <label>Owner<input value={edit.owner} onChange={e => setEdit({ ...edit, owner: e.target.value })} /></label>
                        <label className="admin-edit-form-wide">Notes<textarea rows={2} value={edit.notes} onChange={e => setEdit({ ...edit, notes: e.target.value })} /></label>
                        <div className="admin-edit-form-actions">
                          <button onClick={save}>Save</button>
                          <button onClick={() => setEditingIcao24(null)}>Cancel</button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default AdminAircraftGaps;
